import { sanitizeUrl } from '../api/urlSanitizer';

// Debug diagnostics (the `[http]` request trace of DioApiClient and the
// playback startup trace; spec 11 §2, §11; DATA-005, DATA-021). On in development builds, and in any build with
// `?debug` in the address (remembered for the tab; `?debug=0` turns it off).
// Off in unit tests. Credentials never reach the console: URLs go through
// the URL sanitizer, and no headers are logged.

const DEBUG_KEY = 'plume.debug';
let enabled: boolean | null = null;

function computeEnabled(): boolean {
  let on = import.meta.env.DEV && import.meta.env.MODE !== 'test';
  try {
    if (typeof location !== 'undefined') {
      const flag = new URLSearchParams(location.search).get('debug');
      if (flag != null) {
        on = flag !== '0' && flag !== 'false';
        sessionStorage.setItem(DEBUG_KEY, on ? '1' : '0');
      } else {
        const remembered = sessionStorage.getItem(DEBUG_KEY);
        if (remembered != null) on = remembered === '1';
      }
    }
  } catch {
    // Storage blocked: the build default stands.
  }
  return on;
}

/** Whether debug traces are printed. */
export function debugEnabled(): boolean {
  enabled ??= computeEnabled();
  return enabled;
}

/** Forces debug output on or off (`null` = back to the default). For tests and the console. */
export function setDebugLogging(on: boolean | null): void {
  enabled = on;
}

/** How a traced request ended: its HTTP status, or why there was none. */
export type HttpOutcome = number | 'timeout' | 'network' | 'cancelled';

/** One trace line per request, Flutter's format: `[http] GET 200 123ms <sanitised url>` (+ `type=…` on errors). */
export function formatHttpTrace(method: string, url: string, outcome: HttpOutcome, elapsedMs: number): string {
  const status = typeof outcome === 'number' ? String(outcome) : 'ERR';
  const type = typeof outcome === 'number' ? (outcome >= 400 ? ' type=badResponse' : '') : ` type=${outcome}`;
  return `[http] ${method} ${status} ${Math.max(0, Math.round(elapsedMs))}ms ${sanitizeUrl(url)}${type}`;
}

/** Logs a finished API request (debug builds / `?debug` only). */
export function traceHttp(method: string, url: string, outcome: HttpOutcome, elapsedMs: number): void {
  if (!debugEnabled()) return;
  try {
    console.debug(formatHttpTrace(method, url, outcome, elapsedMs));
  } catch {
    // ignore
  }
}
